import { useState } from 'react';
import { SettingsFrame } from '../components/SettingsFrame';
import { useVertical } from '@/shared/hooks/useVertical';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Badge } from '@/shared/ui';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Modal } from '@/shared/ui/Modal';
import { ConfirmDialog } from '@/shared/ui/Modal/ConfirmDialog';
import { Table, Column } from '@/shared/ui/Table';
import { useAuth } from '@/shared/hooks/useAuth';
import { useLocation } from '@/shared/hooks/useLocation';
import patterns from '@/shared/ui/styles/patterns.module.css';
import {
  staffApi,
  staffRoleOptions,
  isAdminOnlyStaffRoles,
  staffRoleLabel,
  type StaffInput,
  type StaffMember,
  type StaffRole,
} from '../api/staffApi';
import { settingsKeys } from '../queryKeys';
import styles from './SettingsPage.module.css';

interface StaffFormState {
  name: string;
  pronouns: string;
  roles: StaffRole[];
  bbpExposed: boolean;
}

const EMPTY_FORM: StaffFormState = { name: '', pronouns: '', roles: [], bbpExposed: false };

export function StaffPage({ embedded = false }: { embedded?: boolean } = {}) {
  // Role names and which roles exist differ by industry.
  const { verticalId } = useVertical();
  const { user } = useAuth();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [modalStaff, setModalStaff] = useState<StaffMember | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<StaffFormState>(EMPTY_FORM);
  const [deactivateTarget, setDeactivateTarget] = useState<StaffMember | null>(null);

  const isAgency = !!user?.isAgency || user?.role === 'agency_admin';
  const canManage =
    isAgency || user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager';

  const { data, isLoading } = useQuery({
    queryKey: settingsKeys.staff(locationId, includeInactive),
    queryFn: () => staffApi.listStaff(locationId, includeInactive),
    enabled: !!locationId,
  });
  const staff = data?.data ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: settingsKeys.staffBase(locationId) });

  const createStaff = useMutation({
    mutationFn: () => staffApi.createStaff(locationId, formToInput(form)),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
  });

  const updateStaff = useMutation({
    mutationFn: (input: { id: string; patch: ReturnType<typeof formToInput> }) =>
      staffApi.updateStaff(input.id, locationId, input.patch),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
  });

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      staffApi.updateStaff(input.id, locationId, { active: input.active }),
    onSuccess: () => {
      invalidate();
      setDeactivateTarget(null);
    },
  });

  function formToInput(f: StaffFormState) {
    return {
      name: f.name.trim(),
      pronouns: (f.pronouns || null) as StaffInput['pronouns'],
      roles: f.roles,
      bbpExposed: isAdminOnlyStaffRoles(f.roles) ? f.bbpExposed : undefined,
    };
  }

  function openAddModal() {
    setModalStaff(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEditModal(member: StaffMember) {
    setModalStaff(member);
    setForm({
      name: member.name,
      pronouns: member.pronouns ?? '',
      roles: member.roles,
      bbpExposed: member.bbpExposed,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setModalStaff(null);
    setForm(EMPTY_FORM);
  }

  function toggleRole(role: StaffRole) {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role],
    }));
  }

  function handleSave() {
    if (modalStaff) {
      updateStaff.mutate({ id: modalStaff.id, patch: formToInput(form) });
    } else {
      createStaff.mutate();
    }
  }

  const saving = createStaff.isPending || updateStaff.isPending;
  const canSave = form.name.trim().length > 0 && form.roles.length > 0;

  const columns: Column<StaffMember>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => <div className={styles.teamName}>{row.name}</div>,
    },
    {
      key: 'roles',
      header: 'Role(s)',
      render: (row) => (
        <div className={styles.roleBadges}>
          {row.roles.map((r) => (
            <Badge key={r} variant="neutral">
              {staffRoleLabel(r, verticalId)}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.active ? 'success' : 'neutral'}>
          {row.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            render: (row: StaffMember) => (
              <div className={styles.staffActionsCell}>
                <Button variant="tertiary" size="sm" onClick={() => openEditModal(row)}>
                  Edit
                </Button>
                {row.active ? (
                  <Button variant="tertiary" size="sm" onClick={() => setDeactivateTarget(row)}>
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => toggleActive.mutate({ id: row.id, active: true })}
                    loading={toggleActive.isPending}
                  >
                    Reactivate
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <SettingsFrame
      embedded={embedded}
      id="settings-staff"
      pageClassName={styles.page}
      title={embedded ? 'Practice Staff' : 'Staff'}
      subtitle="The people patients meet at this location: providers, hygienists, assistants, sterilization techs, clinical team managers, front desk and office managers. Used to name them in review requests and replies. No sign-in needed."
      actions={
        canManage ? (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={openAddModal}
          >
            Add staff member
          </Button>
        ) : undefined
      }
    >
      <div className={styles.staffToolbar}>
        <label className={styles.inactiveToggle}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      <Table
        columns={columns}
        data={staff}
        loading={isLoading}
        emptyMessage="No staff members yet"
      />

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalStaff ? 'Edit staff member' : 'Add a staff member'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!canSave}
              loading={saving}
            >
              {modalStaff ? 'Save changes' : 'Add staff member'}
            </Button>
          </>
        }
      >
        <div className={patterns.fieldStack}>
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Sara Kim"
            fullWidth
          />
          <Select
            label="Pronouns (optional)"
            value={form.pronouns}
            onChange={(e) => setForm((f) => ({ ...f, pronouns: e.target.value }))}
            options={[
              { value: '', label: 'Not specified' },
              { value: 'she/her', label: 'she/her' },
              { value: 'he/him', label: 'he/him' },
              { value: 'they/them', label: 'they/them' },
            ]}
            hint="Used so AI-drafted reviews refer to this person correctly. When not set, drafts avoid pronouns for them entirely."
            fullWidth
          />
          <div>
            <div className={styles.roleCheckboxLabel}>Role(s)</div>
            <div className={styles.roleCheckboxGroup}>
              {staffRoleOptions(verticalId).map((opt) => (
                <label key={opt.value} className={styles.roleCheckbox}>
                  <input
                    type="checkbox"
                    checked={form.roles.includes(opt.value)}
                    onChange={() => toggleRole(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          {isAdminOnlyStaffRoles(form.roles) && (
            <label className={styles.bbpToggle}>
              <input
                type="checkbox"
                checked={form.bbpExposed}
                onChange={(e) => setForm((f) => ({ ...f, bbpExposed: e.target.checked }))}
              />
              Duties involve handling contaminated items (relevant for Safety &amp; Compliance
              training)
            </label>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() =>
          deactivateTarget && toggleActive.mutate({ id: deactivateTarget.id, active: false })
        }
        title="Deactivate staff member"
        message={`${deactivateTarget?.name} will no longer appear in Safety & Compliance, Reviews, or marketing context until reactivated.`}
        confirmLabel="Deactivate"
        variant="destructive"
        isLoading={toggleActive.isPending}
      />
    </SettingsFrame>
  );
}
